import { useEffect, useState } from 'react';
import { bookingService } from '../../services/booking';
import { generateInvoice } from '../../utils/pdfGenerator';
import Button from '../../components/base/Button';
import { useAuth } from '../../context/AuthContext';

// Helper to safely format any string, object, or array into a readable string for React JSX
const formatErrorMessage = (err: any): string => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
        return err.map((item: any) => {
            if (typeof item === 'string') return item;
            const loc = Array.isArray(item.loc) ? item.loc.slice(1).join('.') : '';
            return `${loc ? loc + ': ' : ''}${item.msg || JSON.stringify(item)}`;
        }).join('; ');
    }
    if (typeof err === 'object') {
        if (err.detail) return formatErrorMessage(err.detail);
        if (err.message) return err.message;
        if (err.error) return err.error;
        return JSON.stringify(err);
    }
    return String(err);
};

export default function Bookings() {
    const { user } = useAuth();
    const [bookings, setBookings] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
    const [retryingId, setRetryingId] = useState<string | null>(null);
    const [retryMessage, setRetryMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [showRawLetsfg, setShowRawLetsfg] = useState(false);
    const [showRawPaystack, setShowRawPaystack] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [manualPnr, setManualPnr] = useState('');
    const [isIssuingManual, setIsIssuingManual] = useState(false);
    const [showManualSection, setShowManualSection] = useState(false);

    const handleDeleteBooking = async (bookingId: string, serviceType?: string, ref?: string) => {
        if (!bookingId) return;
        const confirmMessage = `Are you sure you want to permanently delete this ${serviceType ? serviceType.toUpperCase() : ''} booking?\n\nReference: ${ref || bookingId}\n\nWARNING: This will permanently remove the booking from the database, erase it from the User Dashboard, and remove its amount from Total Bookings and Total Revenue totals.`;
        
        if (!window.confirm(confirmMessage)) return;

        setDeletingId(bookingId);
        try {
            await bookingService.deleteBooking(bookingId);
            setBookings(prev => prev.filter(b => b.id !== bookingId));
            if (selectedBooking && selectedBooking.id === bookingId) {
                setSelectedBooking(null);
            }
            // Re-sync with server so subsequent deletes work smoothly
            await fetchBookings();
        } catch (err: any) {
            console.error('Error deleting booking:', err);
            alert(`Failed to delete booking: ${err.message || 'Server error'}`);
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const data = await bookingService.getBookings();
            setBookings(data || []);
        } catch (error) {
            console.error('Error fetching bookings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRetryLetsfg = async (bookingId: string) => {
        setRetryingId(bookingId);
        setRetryMessage(null);
        try {
            const res: any = await bookingService.retryLetSFGBooking(bookingId);
            if (res.success) {
                setRetryMessage({ type: 'success', text: res.message || 'LetSFG booking fulfilled successfully!' });
                await fetchBookings();
                // Update currently open modal
                if (selectedBooking && selectedBooking.id === bookingId) {
                    setSelectedBooking(res.booking);
                }
            } else {
                setRetryMessage({ type: 'error', text: res.error || 'Retry failed' });
            }
        } catch (error: any) {
            setRetryMessage({ type: 'error', text: error.message || 'Error communicating with LetSFG API' });
            await fetchBookings();
            if (selectedBooking && selectedBooking.id === bookingId) {
                const refreshed = bookings.find(b => b.id === bookingId);
                if (refreshed) setSelectedBooking(refreshed);
            }
        } finally {
            setRetryingId(null);
        }
    };

    const handleManualIssue = async (bookingId: string) => {
        if (!manualPnr.trim()) {
            alert('Please enter a PNR or confirmation code');
            return;
        }
        setIsIssuingManual(true);
        setRetryMessage(null);
        try {
            const res: any = await bookingService.manualIssueBooking(bookingId, manualPnr.trim());
            if (res.success) {
                setRetryMessage({ type: 'success', text: res.message || `Booking manually confirmed with PNR ${manualPnr.trim()}!` });
                await fetchBookings();
                if (selectedBooking && selectedBooking.id === bookingId) {
                    setSelectedBooking(res.booking);
                }
                setManualPnr('');
                setShowManualSection(false);
            } else {
                setRetryMessage({ type: 'error', text: res.error || 'Failed to manually confirm booking' });
            }
        } catch (error: any) {
            setRetryMessage({ type: 'error', text: error.message || 'Error confirming booking' });
        } finally {
            setIsIssuingManual(false);
        }
    };

    const handleUpdateStatus = async (bookingId: string, newStatus: string) => {
        try {
            await bookingService.updateBookingStatus(bookingId, newStatus);
            setRetryMessage({ type: 'success', text: `Booking status updated to ${newStatus.toUpperCase()}` });
            await fetchBookings();
            if (selectedBooking && selectedBooking.id === bookingId) {
                setSelectedBooking({ ...selectedBooking, status: newStatus });
            }
        } catch (err: any) {
            console.error('Error updating status:', err);
            setRetryMessage({ type: 'error', text: err.message || 'Failed to update status' });
        }
    };

    const handleProcessRefund = async (bookingId: string) => {
        if (window.confirm('Are you sure you want to process a refund via Paystack? This action cannot be undone.')) {
            try {
                const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-refund`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        bookingId,
                        adminEmail: user?.email
                    })
                });

                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result.error || 'Refund failed');
                }

                alert('Refund processed successfully via Paystack!');
                fetchBookings();
                setSelectedBooking(null);
            } catch (error: any) {
                console.error('Error processing refund:', error);
                alert(`Failed to process refund: ${error.message}`);
            }
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount: number, currency: string = 'NGN') => {
        if (currency === 'NGN') return `₦${Number(amount || 0).toLocaleString()}`;
        return `$${Number(amount || 0).toLocaleString()}`;
    };

    const parseDetails = (booking: any) => {
        if (!booking) return {};
        try {
            return typeof booking.booking_details === 'string'
                ? JSON.parse(booking.booking_details || '{}')
                : (booking.booking_details || {});
        } catch {
            return {};
        }
    };

    const getStatusBadge = (status: string) => {
        const s = (status || '').toLowerCase();
        if (s === 'confirmed' || s === 'ticketed' || s === 'completed' || s === 'authorised') {
            return <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-green-500/15 text-green-400 border border-green-500/30">CONFIRMED</span>;
        }
        if (s === 'paid') {
            return <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/30">PAID</span>;
        }
        if (s === 'fulfillment_failed') {
            return (
                <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/30 flex items-center gap-1.5" title="Payment was confirmed via Paystack; LetSFG ticketing pending">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                    PAID <span className="text-amber-400 font-normal text-[10px]">(LetSFG Pending)</span>
                </span>
            );
        }
        if (s === 'payment_failed') {
            return <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-rose-950 text-rose-300 border border-rose-800">PAYMENT FAILED</span>;
        }
        if (s === 'pending_fulfillment') {
            return <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-amber-500/15 text-amber-400 border border-amber-500/30">PROCESSING</span>;
        }
        if (s === 'refunded') {
            return <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-gray-500/15 text-gray-400 border border-gray-500/30">REFUNDED</span>;
        }
        return <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 capitalize">{s.replace('_', ' ')}</span>;
    };

    if (loading) return <div className="p-8 text-center text-white">Loading live bookings...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight">Bookings & Live Transactions</h1>
                    <p className="text-sm text-gray-400 mt-1">
                        Track live Paystack payments and LetSFG airline/hotel ticketing fulfillment
                    </p>
                </div>
                <button
                    onClick={fetchBookings}
                    className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-logo-gold-400 rounded-xl text-sm font-semibold flex items-center gap-2 border border-gray-700 transition-colors"
                >
                    <i className="ri-refresh-line"></i> Refresh
                </button>
            </div>

            {/* Bookings Table */}
            <div className="bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-800">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-800">
                        <thead className="bg-gray-950/80">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date & ID</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Service</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">PNR / Ref</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800 bg-gray-900">
                            {bookings.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                                        No bookings recorded yet.
                                    </td>
                                </tr>
                            ) : (
                                bookings.map((booking) => {
                                    const details = parseDetails(booking);
                                    const pnr = details.pnr || details.letsfg_booking_id || null;
                                    const paymentRef = details.payment_reference || 'N/A';
                                    const email = details.contact_email || details.email || booking.user_email || 'N/A';
                                    const phone = details.contact_phone || details.phone || 'N/A';

                                    return (
                                        <tr key={booking.id} className="hover:bg-gray-800/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-semibold text-white">{formatDate(booking.created_at)}</div>
                                                <div className="text-xs font-mono text-gray-500">#{booking.id.substring(0, 8)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-white">{email}</div>
                                                <div className="text-xs text-gray-400">{phone}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-xs font-bold uppercase tracking-wider text-logo-gold-400 bg-logo-gold-400/10 px-2 py-1 rounded-md border border-logo-gold-400/20">
                                                    {booking.service_type || 'flight'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-white">
                                                {formatCurrency(Number(booking.amount || 0), booking.currency || 'NGN')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {pnr ? (
                                                    <span className="text-xs font-mono font-bold text-amber-400 bg-amber-400/10 px-2.5 py-1 rounded-lg border border-amber-400/30">
                                                        {pnr}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs font-mono text-gray-400">{paymentRef.substring(0, 14)}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(booking.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedBooking(booking);
                                                            setRetryMessage(null);
                                                        }}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/15 transition-colors cursor-pointer"
                                                    >
                                                        View Details
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteBooking(booking.id, booking.service_type, pnr || paymentRef)}
                                                        disabled={deletingId === booking.id}
                                                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 hover:bg-red-500/25 text-red-400 border border-red-500/25 transition-colors cursor-pointer flex items-center gap-1 disabled:opacity-50"
                                                        title="Delete booking permanently"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                        <span>{deletingId === booking.id ? 'Deleting...' : 'Delete'}</span>
                                                    </button>
                                                </div>
                                             </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Details Modal */}
            {selectedBooking && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-gray-900 border border-gray-800 rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl text-white">
                        
                        {/* Modal Header */}
                        <div className="bg-gray-950 px-6 py-5 flex justify-between items-center sticky top-0 z-20 border-b border-gray-800">
                            <div>
                                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                    <i className="ri-file-list-3-line text-logo-gold-400"></i>
                                    Booking & Transaction Overview
                                </h2>
                                <p className="text-xs text-gray-400 mt-0.5 font-mono">ID: {selectedBooking.id}</p>
                            </div>
                            <div className="flex items-center space-x-2 flex-wrap gap-y-2">
                                {selectedBooking.status !== 'paid' && (
                                    <button
                                        onClick={() => handleUpdateStatus(selectedBooking.id, 'paid')}
                                        className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/30 px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center transition-colors cursor-pointer"
                                        title="Mark payment as verified and status as Paid"
                                    >
                                        <i className="ri-check-line mr-1"></i>
                                        Set to Paid
                                    </button>
                                )}
                                {selectedBooking.status !== 'confirmed' && (
                                    <button
                                        onClick={() => handleUpdateStatus(selectedBooking.id, 'confirmed')}
                                        className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center transition-colors cursor-pointer"
                                        title="Mark booking as Confirmed"
                                    >
                                        <i className="ri-check-double-line mr-1"></i>
                                        Set to Confirmed
                                    </button>
                                )}
                                {selectedBooking.status === 'refund_pending' && (
                                    <Button
                                        size="sm"
                                        className="bg-orange-500 hover:bg-orange-600 text-white"
                                        onClick={() => handleProcessRefund(selectedBooking.id)}
                                    >
                                        Process Refund
                                    </Button>
                                )}
                                <button
                                    onClick={() => generateInvoice(selectedBooking)}
                                    className="bg-white/10 hover:bg-white/20 text-white px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center border border-white/15 transition-colors"
                                >
                                    <i className="ri-download-line mr-1.5"></i>
                                    Invoice PDF
                                </button>
                                <button
                                    onClick={() => handleDeleteBooking(selectedBooking.id, selectedBooking.service_type, parseDetails(selectedBooking).pnr || selectedBooking.payment_reference)}
                                    disabled={deletingId === selectedBooking.id}
                                    className="bg-red-500/15 hover:bg-red-500/30 text-red-400 border border-red-500/30 px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center transition-colors cursor-pointer disabled:opacity-50"
                                    title="Delete booking permanently"
                                >
                                    <i className="ri-delete-bin-line mr-1.5"></i>
                                    {deletingId === selectedBooking.id ? 'Deleting...' : 'Delete Booking'}
                                </button>
                                <button
                                    onClick={() => setSelectedBooking(null)}
                                    className="text-gray-400 hover:text-white p-2 rounded-lg transition-colors cursor-pointer"
                                >
                                    <i className="ri-close-line text-2xl"></i>
                                </button>
                            </div>
                        </div>

                        <div className="p-6 md:p-8 space-y-6">

                            {/* Status Notification Banner */}
                            {(() => {
                                const details = parseDetails(selectedBooking);
                                const isFulfillmentFailed = selectedBooking.status === 'fulfillment_failed';
                                const isPaymentFailed = selectedBooking.status === 'payment_failed';
                                const isConfirmed = selectedBooking.status === 'confirmed' || selectedBooking.status === 'ticketed';

                                if (isConfirmed) {
                                    return (
                                        <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/30 flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-green-500/20 text-green-400 flex items-center justify-center flex-shrink-0">
                                                    <i className="ri-checkbox-circle-fill text-2xl"></i>
                                                </div>
                                                <div>
                                                    <p className="font-bold text-green-300">Live Booking Confirmed</p>
                                                    <p className="text-xs text-green-400/80">Paystack payment verified and booking successfully confirmed.</p>
                                                </div>
                                            </div>
                                            {details.pnr && (
                                                <div className="text-right">
                                                    <span className="text-[10px] uppercase text-gray-400 block">PNR / Ref</span>
                                                    <span className="text-lg font-mono font-bold text-amber-400">{details.pnr}</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                const hasLetSfgPending = isFulfillmentFailed || (selectedBooking.status === 'paid' && details.letsfg_error);
                                if (hasLetSfgPending) {
                                    return (
                                        <div className="p-5 rounded-2xl bg-amber-500/15 border border-amber-500/30 space-y-3">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        <i className="ri-alert-line text-2xl"></i>
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-amber-300">Payment Confirmed · LetSFG Ticketing Pending</p>
                                                        <p className="text-xs text-amber-200/90 mt-1 break-words">
                                                            {formatErrorMessage(details.letsfg_error) || 'Payment received successfully via Paystack. Click below to retry LetSFG ticketing or confirm manually.'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleRetryLetsfg(selectedBooking.id)}
                                                    disabled={retryingId === selectedBooking.id}
                                                    className="px-4 py-2.5 rounded-xl bg-logo-gold-600 hover:bg-logo-gold-500 text-white font-bold text-xs shadow-lg flex items-center gap-2 cursor-pointer disabled:opacity-50 flex-shrink-0"
                                                >
                                                    {retryingId === selectedBooking.id ? (
                                                        <>
                                                            <i className="ri-loader-4-line animate-spin"></i>
                                                            Connecting...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ri-repeat-line"></i>
                                                            Retry LetSFG Booking
                                                        </>
                                                    )}
                                                </button>
                                            </div>

                                            {retryMessage && (
                                                <div className={`p-3 rounded-xl text-xs font-medium ${retryMessage.type === 'success' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                                                    {formatErrorMessage(retryMessage.text)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }

                                if (isPaymentFailed) {
                                    return (
                                        <div className="p-4 rounded-2xl bg-rose-950/60 border border-rose-800 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-rose-900/50 text-rose-400 flex items-center justify-center flex-shrink-0">
                                                <i className="ri-close-circle-fill text-2xl"></i>
                                            </div>
                                            <div>
                                                <p className="font-bold text-rose-300">Paystack Payment Declined or Incomplete</p>
                                                <p className="text-xs text-rose-400/80 mt-0.5">{formatErrorMessage(details.paystack_error) || 'Payment transaction was not completed.'}</p>
                                            </div>
                                        </div>
                                    );
                                }

                                if (selectedBooking.status === 'paid') {
                                    return (
                                        <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center flex-shrink-0">
                                                    <i className="ri-checkbox-circle-line text-2xl"></i>
                                                </div>
                                                <div>
                                                    <p className="font-bold text-blue-300">Paystack Payment Confirmed (Paid)</p>
                                                    <p className="text-xs text-blue-300/80">Funds successfully received into your Paystack account. Reservation is ready for LetSFG or manual issuance.</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return null;
                            })()}

                            {/* Two Column Grid: Paystack Info vs LetSFG Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* Card 1: Paystack Live Payment */}
                                {(() => {
                                    const details = parseDetails(selectedBooking);
                                    const paystack = details.paystack_data || {};
                                    return (
                                        <div className="bg-gray-950/60 p-5 rounded-2xl border border-gray-800 space-y-4">
                                            <div className="flex items-center justify-between pb-3 border-b border-gray-800">
                                                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                                    <i className="ri-bank-card-line text-green-400"></i>
                                                    Paystack Payment Record
                                                </h3>
                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                                    paystack.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                                }`}>
                                                    {(paystack.status || selectedBooking.status).toUpperCase()}
                                                </span>
                                            </div>

                                            <div className="space-y-2.5 text-xs text-gray-300">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Payment Reference:</span>
                                                    <span className="font-mono text-white">{details.payment_reference || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Amount Charged:</span>
                                                    <span className="font-bold text-green-400">{formatCurrency(Number(selectedBooking.amount || 0), selectedBooking.currency)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Payment Channel:</span>
                                                    <span className="font-medium text-white capitalize">{paystack.channel || 'Card / Web'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Gateway Response:</span>
                                                    <span className="font-medium text-white">{paystack.gateway_response || 'Approved'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Paid At:</span>
                                                    <span className="text-gray-300">{formatDate(paystack.paid_at || selectedBooking.created_at)}</span>
                                                </div>
                                            </div>

                                            {/* Collapsible Paystack Data */}
                                            {details.paystack_data && (
                                                <div className="pt-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowRawPaystack(!showRawPaystack)}
                                                        className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1 cursor-pointer"
                                                    >
                                                        <i className={showRawPaystack ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
                                                        {showRawPaystack ? 'Hide Raw Paystack JSON' : 'View Raw Paystack JSON'}
                                                    </button>
                                                    {showRawPaystack && (
                                                        <pre className="mt-2 p-3 rounded-xl bg-black/60 text-[11px] text-gray-300 font-mono overflow-x-auto max-h-40 overflow-y-auto border border-gray-800">
                                                            {JSON.stringify(details.paystack_data, null, 2)}
                                                        </pre>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Card 2: LetSFG API Integration */}
                                {(() => {
                                    const details = parseDetails(selectedBooking);
                                    const letsfg = details.letsfg_response || {};
                                    return (
                                        <div className="bg-gray-950/60 p-5 rounded-2xl border border-gray-800 space-y-4">
                                            <div className="flex items-center justify-between pb-3 border-b border-gray-800">
                                                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                                    <i className="ri-plane-line text-logo-gold-400"></i>
                                                    LetSFG Account Fulfillment
                                                </h3>
                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                                    details.pnr ? 'bg-amber-400/20 text-amber-300' : 'bg-gray-800 text-gray-400'
                                                }`}>
                                                    {details.pnr ? 'TICKETED' : (selectedBooking.status || 'PENDING').toUpperCase()}
                                                </span>
                                            </div>

                                            <div className="space-y-2.5 text-xs text-gray-300">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-500">Booking Reference (PNR):</span>
                                                    <span className="font-mono font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded border border-amber-400/20">
                                                        {details.pnr || 'Awaiting Issuance'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">LetSFG Booking ID:</span>
                                                    <span className="font-mono text-white">{details.letsfg_booking_id || letsfg.booking_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">LetSFG Offer ID:</span>
                                                    <span className="font-mono text-gray-400">{details.offer_id || 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-500">Search Session ID:</span>
                                                    <span className="font-mono text-gray-400">{details.search_id || 'N/A'}</span>
                                                </div>
                                            </div>

                                            {/* Action to Retry or Re-query */}
                                            <div className="pt-2 flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRetryLetsfg(selectedBooking.id)}
                                                        disabled={retryingId === selectedBooking.id}
                                                        className="px-3 py-1.5 rounded-lg bg-logo-gold-600 hover:bg-logo-gold-700 text-white text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow"
                                                    >
                                                        <i className={retryingId === selectedBooking.id ? "ri-loader-4-line animate-spin" : "ri-repeat-line"}></i>
                                                        {retryingId === selectedBooking.id ? 'Connecting...' : 'Call LetSFG API'}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => setShowManualSection(!showManualSection)}
                                                        className="px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-medium border border-gray-700 transition-colors flex items-center gap-1 cursor-pointer"
                                                    >
                                                        <i className="ri-edit-line"></i>
                                                        {showManualSection ? 'Cancel Manual PNR' : 'Manual PNR'}
                                                    </button>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setShowRawLetsfg(!showRawLetsfg)}
                                                    className="text-[11px] text-gray-400 hover:text-white flex items-center gap-1 cursor-pointer"
                                                >
                                                    <i className={showRawLetsfg ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
                                                    {showRawLetsfg ? 'Hide LetSFG JSON' : 'View LetSFG JSON'}
                                                </button>
                                            </div>

                                            {showManualSection && (
                                                <div className="mt-3 p-3 bg-gray-900/90 rounded-xl border border-gray-700 space-y-2">
                                                    <label className="text-[11px] font-semibold text-gray-300 block">
                                                        Enter Confirmation Code / Airline PNR / Hotel Booking Reference:
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. RIG78492 or HTL_982314"
                                                            value={manualPnr}
                                                            onChange={e => setManualPnr(e.target.value)}
                                                            className="bg-black/50 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white uppercase font-mono flex-1 focus:outline-none focus:border-logo-gold-500"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleManualIssue(selectedBooking.id)}
                                                            disabled={isIssuingManual || !manualPnr.trim()}
                                                            className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs font-bold transition-colors disabled:opacity-50 cursor-pointer"
                                                        >
                                                            {isIssuingManual ? 'Confirming...' : 'Confirm'}
                                                        </button>
                                                    </div>
                                                    <p className="text-[10px] text-gray-400">
                                                        Use this if LetSFG API is temporarily unreachable (e.g. Cloudflare 504) and you want to confirm the ticket manually.
                                                    </p>
                                                </div>
                                            )}

                                            {retryMessage && (
                                                <div className={`mt-3 p-2.5 rounded-xl text-xs font-medium border ${
                                                    retryMessage.type === 'success' ? 'bg-green-500/15 text-green-300 border-green-500/30' : 'bg-red-500/15 text-red-300 border-red-500/30'
                                                }`}>
                                                    {formatErrorMessage(retryMessage.text)}
                                                </div>
                                            )}

                                            {details.letsfg_error && (
                                                <div className="mt-3 p-2.5 rounded-xl bg-amber-950/40 border border-amber-800/60 text-xs text-amber-200">
                                                    <span className="font-semibold block text-amber-400 mb-1 flex items-center gap-1">
                                                        <i className="ri-information-line"></i> LetSFG API Status / Notice:
                                                    </span>
                                                    <span className="font-mono text-[11px] block break-words">
                                                        {formatErrorMessage(details.letsfg_error)}
                                                    </span>
                                                </div>
                                            )}

                                            {showRawLetsfg && (
                                                <pre className="mt-2 p-3 rounded-xl bg-black/60 text-[11px] text-gray-300 font-mono overflow-x-auto max-h-40 overflow-y-auto border border-gray-800">
                                                    {JSON.stringify(details.letsfg_response || details.letsfg_error || 'No response recorded', null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Passenger Details */}
                            <div className="bg-gray-950/60 p-5 rounded-2xl border border-gray-800">
                                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                                    <i className="ri-user-star-line text-logo-gold-400"></i>
                                    Passenger & Contact Information
                                </h3>
                                {(() => {
                                    const details = parseDetails(selectedBooking);
                                    const passengers = details.passengers || [];

                                    return (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-4 border-b border-gray-800 text-xs">
                                                <div>
                                                    <span className="text-gray-500 block">Contact Email:</span>
                                                    <span className="font-semibold text-white">{details.contact_email || selectedBooking.user_email || 'N/A'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500 block">Contact Phone:</span>
                                                    <span className="font-semibold text-white">{details.contact_phone || 'N/A'}</span>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {passengers.length === 0 ? (
                                                    <p className="text-xs text-gray-500 italic">No passenger details recorded.</p>
                                                ) : (
                                                    passengers.map((p: any, i: number) => {
                                                        if (!p || typeof p !== 'object') return null;
                                                        const pName = `${p.given_name || p.firstName || ''} ${p.family_name || p.lastName || ''}`.trim() || p.name || 'Guest';
                                                        return (
                                                            <div key={i} className="bg-gray-900 p-3.5 rounded-xl border border-gray-800 text-xs space-y-1.5">
                                                                <div className="flex items-center gap-2 font-bold text-white">
                                                                    <span className="w-5 h-5 rounded-full bg-logo-gold-500/20 text-logo-gold-400 text-[10px] flex items-center justify-center">
                                                                        {i + 1}
                                                                    </span>
                                                                    <span>{pName}</span>
                                                                </div>
                                                            <div className="grid grid-cols-2 gap-1 text-[11px] text-gray-400 pt-1">
                                                                <div>Passport: <span className="text-gray-200 font-mono">{p.passport_number || p.passportNumber || 'N/A'}</span></div>
                                                                <div>Nationality: <span className="text-gray-200">{p.nationality || 'NG'}</span></div>
                                                                <div>DOB: <span className="text-gray-200">{p.born_on || p.dob || 'N/A'}</span></div>
                                                                <div>Gender: <span className="text-gray-200 capitalize">{p.gender || 'N/A'}</span></div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
